import { RedoCheckbox } from "@redotech/arbiter/checkbox/redo-checkbox";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { getCurrencyFormatter } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import ShieldTickIcon from "@redotech/redo-icons/shield-tick_thick.svg";
import {
  EWWidgetTextProps,
  ExtendedWarrantyDataTarget,
  getCheapestOffering,
  NO_COVERAGE_TIME_ID,
  Offering,
} from "@redotech/redo-return-warranties-common/extended-warranty";
import * as extendedWarrantyCss from "@redotech/redo-web/expanded-warranties/extended-warranty.module.css";
import htmlReactParser from "html-react-parser";
import { memo } from "react";

export const ExtendedWarrantyCheckbox = memo(function ExtendedWarrantyCheckbox({
  offerings,
  selectedOffering,
  onTileClick,
  onSeeDetailsClick,
  text,
}: {
  offerings: Offering[];
  selectedOffering?: Offering;
  onTileClick: (offering: Offering) => void;
  onSeeDetailsClick: () => void;
  text: EWWidgetTextProps;
}) {
  const cheapestOffering = getCheapestOffering(offerings);
  const noCoverageOffering = offerings.find(
    (o) => o.id === NO_COVERAGE_TIME_ID,
  );

  const noCoverageOfferingSelected =
    selectedOffering?.id === NO_COVERAGE_TIME_ID;
  const shouldShowCheapest = !selectedOffering || noCoverageOfferingSelected;
  const offeringToShow = shouldShowCheapest
    ? cheapestOffering
    : selectedOffering;
  const isSelected = !shouldShowCheapest;

  const handleCheckboxChange = useHandler((value: boolean) => {
    if (!offeringToShow || !noCoverageOffering) {
      return;
    }

    if (value) {
      onTileClick(offeringToShow);
    } else {
      onTileClick(noCoverageOffering);
    }
  });

  if (!offeringToShow) {
    return null;
  }

  const formattedPrice = getCurrencyFormatter(
    offeringToShow.price.currency,
  ).format(offeringToShow.price.amount);

  const checkboxText = text.pdpCheckboxTitle
    .replace("%plan_title%", offeringToShow.title)
    .replace("%price%", formattedPrice);

  return (
    <Flex
      align="center"
      data-target={ExtendedWarrantyDataTarget.CHECKBOX_CONTAINER}
      gap="sm"
      justify="space-between"
      w="full"
      wrap="nowrap"
    >
      <Flex align="center" gap="sm">
        <Flex data-target={ExtendedWarrantyDataTarget.CHECKBOX_INPUT}>
          <RedoCheckbox
            setValue={handleCheckboxChange}
            size="sm"
            value={isSelected}
          />
        </Flex>
        <ShieldTickIcon
          data-target={ExtendedWarrantyDataTarget.CHECKBOX_SHIELD}
        />
        <Flex
          data-target={ExtendedWarrantyDataTarget.CHECKBOX_CONTENT}
          flexDirection="column"
          gap="none"
        >
          <Text
            data-target={ExtendedWarrantyDataTarget.CHECKBOX_TEXT}
            fontSize="sm"
            fontWeight="medium"
          >
            {htmlReactParser(checkboxText)}
          </Text>
        </Flex>
      </Flex>
      <a
        className={extendedWarrantyCss.learnMore}
        data-target={ExtendedWarrantyDataTarget.SEE_DETAILS_LINK}
        onClick={onSeeDetailsClick}
      >
        See Details
      </a>
    </Flex>
  );
});
